import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  dismissTrend,
  fetchCurrentTrends,
  trendCtaClick,
  type RecruiterTrendCard,
} from './activityFeedApi';
import { defaultTrendCtaLabel, trendCtaHref } from '../../utils/trendCta';
import { useDashboardRealtimeRefresh } from '../../hooks/useDashboardRealtimeRefresh';
import { ProfileTipsModal } from './ProfileTipsModal';

const MAX_PREVIEW_TRENDS = 2;

function formatTrendDate(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export function CurrentTrends() {
  const [trends, setTrends] = useState<RecruiterTrendCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [profileTipsOpen, setProfileTipsOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await fetchCurrentTrends();
      setTrends(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load trends');
      setTrends([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useDashboardRealtimeRefresh(load);

  const visible = useMemo(() => (trends ?? []).slice(0, MAX_PREVIEW_TRENDS), [trends]);
  const hasMore = (trends?.length ?? 0) > MAX_PREVIEW_TRENDS;
  const totalCount = trends?.length ?? 0;
  const viewAllLabel = totalCount > 0 ? `View all${hasMore ? ` (${totalCount})` : ''} ›` : null;

  if (trends === null) {
    return (
      <article className="v1-card v1-dash-trends-span flex h-full min-h-0 flex-col">
        <Header />
        <div className="mt-2.5 space-y-1.5">
          {[1, 2].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      </article>
    );
  }

  if (error) {
    return (
      <article className="v1-card v1-dash-trends-span flex h-full min-h-0 flex-col">
        <Header />
        <p className="mt-2.5 text-xs text-red-600">{error}</p>
      </article>
    );
  }

  if (trends.length === 0) {
    return (
      <>
        <article className="v1-card v1-dash-trends-span v1-card-trends-empty">
          <div className="v1-trends-empty">
            <div className="v1-trends-empty-text">
              <h3 className="v1-card-heading v1-card-heading-sm">Current Trends</h3>
              <p className="text-[11px] text-slate-600 mt-0.5">
                No new trends right now — check back after the next digest.
              </p>
            </div>
            <button
              type="button"
              className="v1-btn v1-btn-outline v1-btn-sm"
              onClick={() => setProfileTipsOpen(true)}
            >
              Profile tips
            </button>
          </div>
        </article>
        <ProfileTipsModal show={profileTipsOpen} onHide={() => setProfileTipsOpen(false)} />
      </>
    );
  }

  return (
    <article className="v1-card v1-dash-trends-span flex h-full min-h-0 flex-col">
      <Header viewAllLabel={viewAllLabel} />

      <div className="mt-2.5 flex min-h-0 flex-1 flex-col gap-1.5">
        {visible.map((t) => (
          <TrendCard key={t.id} trend={t} onDismissed={() => void load()} />
        ))}
      </div>
    </article>
  );
}

function Header({ viewAllLabel }: { viewAllLabel?: string | null }) {
  return (
    <div className="border-b border-emerald-100/80 pb-2.5">
      <div className="flex items-start justify-between gap-2">
        <h3 className="v1-card-heading v1-card-heading-sm">Current Trends</h3>
        {viewAllLabel ? (
          <Link
            to="/dashboard/trends"
            className="v1-link-btn v1-link-btn-inline shrink-0 text-[12px]"
          >
            {viewAllLabel}
          </Link>
        ) : null}
      </div>
      <p className="v1-card-muted mt-1 text-[12px] leading-snug">
        Recruiter behavior on SkipScreen — synthesized for your next move.
      </p>
    </div>
  );
}

function TrendCard({ trend, onDismissed }: { trend: RecruiterTrendCard; onDismissed: () => void }) {
  const navigate = useNavigate();
  const [dismissing, setDismissing] = useState(false);

  const ctaLabel = trend.cta_label?.trim() || defaultTrendCtaLabel(trend.cta_type);

  async function dismiss() {
    if (dismissing) return;
    setDismissing(true);
    try {
      await dismissTrend(trend.id);
      onDismissed();
    } finally {
      setDismissing(false);
    }
  }

  async function onCta() {
    await trendCtaClick(trend.id);
    navigate(trendCtaHref(trend.cta_type, trend.cta_target));
  }

  return (
    <div className="rounded-lg border border-emerald-100/90 bg-emerald-50/20 px-3 py-2.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {formatTrendDate(trend.published_at)} · Trend
        </p>
        <button
          type="button"
          className="ml-auto inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-rose-200 bg-rose-50 text-[14px] font-semibold leading-none text-rose-600 shadow-sm transition-colors hover:border-rose-300 hover:bg-rose-100 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:opacity-60"
          aria-label="Dismiss trend"
          disabled={dismissing}
          onClick={() => void dismiss()}
        >
          ×
        </button>
      </div>
      <p className="mt-1 line-clamp-1 text-[14px] font-semibold leading-snug text-slate-900">
        {trend.title}
      </p>
      <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-slate-600">
        {trend.trend_summary}
      </p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <Link
          to="/dashboard/trends"
          className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 transition-colors hover:text-emerald-900"
        >
          Details →
        </Link>
        <button
          type="button"
          className="v1-btn v1-btn-primary v1-btn-sm shrink-0 px-3 py-1 text-[12px]"
          onClick={() => void onCta()}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}
